import type { Nullable } from '@src/shared/types/common';

/**
 * Утилита для нормализации значений фильтров, прочитанных из URL query-параметров.
 *
 * Используется совместно с window.getQueryParams() внутри функций filters()
 * на страницах с гридами, чтобы не зависеть от асинхронной загрузки dataSource контролов.
 *
 * @example
 * function filters() {
 *     const query = window.getQueryParams();
 *     return {
 *         Contractor: FilterPrepare.toArray(query.msContractor),
 *         OwnSoftware: FilterPrepare.toString(query.cbOwnSoftware, '-1'),
 *         PeriodType: FilterPrepare.toNumber(query.ddPeriodType, 2),
 *         Cycle: FilterPrepare.toBool(query.cbCycle),
 *     };
 * }
 */
export class FilterPrepare {
    /** Маркер, которым collectFilters помечает пустые контролы */
    static readonly noValue = 'novalue';

    /** Маркер выбора всех значений в мультиселекте */
    static readonly ciAll = 'CI_All';

    static readonly idAll = '-1';

    /**
     * Нормализует значение мультиселекта из URL в массив чисел.
     *
     * - `'CI_All'` (collectFilters пишет при выборе всех значений) → `[]`
     * - `'novalue'` (пользователь явно сбросил) → `[]`
     * - выбор «Все» с id `-1` (`'-1'` или значение содержит `'-1'`) → `[]`
     * - `undefined` (ключ отсутствует в URL, первая загрузка) → `fallback`
     * - иначе → `value.split(',')` с декодированием и преобразованием каждого элемента в число
     *
     * @param value — значение из query-параметров (напр. `query.msContractor`)
     * @param fallback — значение по умолчанию, если ключ отсутствует в URL
     * @returns массив числовых значений
     */
    static toArray(value: string | undefined, fallback: number[] = []): number[] {
        if (value === undefined) {
            return fallback;
        }

        if (value === FilterPrepare.noValue
            || value === FilterPrepare.ciAll
            || value === FilterPrepare.idAll
            || value.includes(FilterPrepare.idAll)) {
            return [];
        }

        return value
            .split(',')
            .filter((v) => v !== '' && !Number.isNaN(v))
            .map((v) => Number(decodeURIComponent(v.trim())));
    }

    /**
     * Нормализует значение мультиселекта со строковыми значениями из URL в массив строк.
     *
     * Нужен для мультиселектов, у которых значения — не идентификаторы, а текст
     * (`DataValueField = DataTextField = nameof(SimpleDto.Name)`).
     *
     * - `'CI_All'` / `'novalue'` → `[]`
     * - `undefined` (ключ отсутствует в URL) → `fallback`
     * - иначе → `value.split(',')` с декодированием каждого элемента
     *
     * @param value — значение из query-параметров (напр. `query.msZoomAccounts`)
     * @param fallback — значение по умолчанию, если ключ отсутствует в URL
     * @returns массив строковых значений
     */
    static toStringArray(value: string | undefined, fallback: string[] = []): string[] {
        if (value === undefined) {
            return fallback;
        }

        if (value === FilterPrepare.noValue || value === FilterPrepare.ciAll) {
            return [];
        }

        return value
            .split(',')
            .filter((v) => v !== '')
            .map((v) => decodeURIComponent(v.trim()));
    }

    /**
     * Нормализует значение комбобокса / дропдауна из URL в строку.
     *
     * - `'novalue'` → `''` (пустая строка)
     * - `undefined` → `fallback`
     * - иначе → `decodeURI(value)`
     *
     * @param value — значение из query-параметров (напр. `query.cbOwnSoftware`)
     * @param fallback — значение по умолчанию, если ключ отсутствует в URL
     * @returns выбранное значение или пустая строка
     */
    static toString(value: string | undefined, fallback: string = ''): string {
        if (value === FilterPrepare.noValue) {
            return '';
        }
        if (value === undefined) {
            return fallback;
        }

        return decodeURI(value);
    }

    /**
     * Нормализует значение комбобокса из URL в число.
     *
     * Подходит для комбобоксов, привязанных к DTO-полям типа `int?` / `DateFilter?`.
     * Заменяет паттерн `window.NumberOrNull(cb.value())` и `Number(cb.value())`.
     *
     * - `'novalue'` / `''` → `fallback`
     * - `undefined` → `fallback`
     * - иначе → `Number(value)`, если `NaN` → `fallback`
     *
     * @param value — значение из query-параметров (напр. `query.ddPeriodType`)
     * @param fallback — значение по умолчанию, если ключ отсутствует или не является числом
     * @returns числовое значение или null
     */
    static toNumber(value: string | undefined, fallback: Nullable<number> = null): Nullable<number> {
        if (value === undefined || value === '') {
            return fallback;
        }

        if (value === FilterPrepare.noValue) {
            return null;
        }

        const num = Number(value);
        return isNaN(num) ? fallback : num;
    }

    /**
     * Нормализует значение бинарного комбобокса (Да/Нет/Все) из URL в boolean.
     *
     * Для `back-binary-combobox` с `with-all="true"`, где values: `''` (Все), `'true'`, `'false'`.
     * В URL через collectFilters записываются: `'novalue'`, `'true'`, `'false'`.
     *
     * - `'true'` → `true`
     * - `'false'` → `false`
     * - `'novalue'` / `''` → `null` (выбрано «Все» или сброшено)
     * - `undefined` → `fallback`
     *
     * @param value — значение из query-параметров (напр. `query.cbCycle`)
     * @param fallback — значение по умолчанию, если ключ отсутствует в URL
     * @returns `true` / `false` / `null`
     */
    static toBool(value: string | undefined, fallback: Nullable<boolean> = null): Nullable<boolean> {
        if (value === undefined) {
            return fallback;
        }
        if (value === 'true') {
            return true;
        }
        if (value === 'false') {
            return false;
        }

        return null;
    }

    /**
     * Нормализует значение `.BinaryComboBox()` (Да/Нет/Все: 1/0/-1) из URL в boolean для DTO-полей `bool?`.
     *
     * - `'-1'` (Все) → `null`
     * - `'1'` (Да) → `true`
     * - `'0'` (Нет) → `false`
     * - `'novalue'` / `''` → `null`
     * - `undefined` → fallback (по умолчанию `'-1'`)
     *
     * @param value — значение из query-параметров (напр. `query.cbMds`)
     * @param fallback — значение «Все» по умолчанию, если ключ отсутствует в URL
     */
    static toBinaryCombo(value: string | undefined, fallback: string = '-1'): Nullable<boolean> {
        const normalized = FilterPrepare.toString(value, fallback);

        if (normalized === '-1') {
            return null;
        }
        if (normalized === '1') {
            return true;
        }
        if (normalized === '0') {
            return false;
        }

        return null;
    }
}

window.FilterPrepare = FilterPrepare;

